import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Group, Paper, Stack, Table, Text, TextInput, Title, createStyles} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  requireScientificModel,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import GetInTouchButton from './contact-us/GetInTouchButton';
import type {SortDirection} from './lib/listingTypes';
import ModelHubEmptyState from './lib/ModelHubEmptyState';
import {BATCHES_ILLUSTRATION} from './lib/modelHubIllustrations';
import {
  MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING,
  useModelHubListingTableStyles,
} from './lib/modelHubListingTableStyles';
import ModelHubLoadingState from './lib/ModelHubLoadingState';
import ModelHubNoSearchResults from './lib/ModelHubNoSearchResults';
import SortableColumnHeader from './lib/SortableColumnHeader';
import StatusBadge from './lib/StatusBadge';
import TablePagination from './lib/TablePagination';
import useBatchesSearch from './lib/useBatchesSearch';
import {MODEL_HUB_MAIN_PADDING_Y_PX} from './ModelHubConstants';

type BatchSortField = 'createdAt' | 'modifiedAt' | 'name';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface BatchesPageProps {
  catalogByExternalReference: ScientificModelsByExternalReference;
  /** Omitted when signed out, which is what hides the feedback entry point. */
  onContactUs?: () => void;
  onNewPrediction: () => void;
  onSelectBatch: (batchId: string, name: string) => void;
}

export default function BatchesPage({
  catalogByExternalReference,
  onContactUs,
  onNewPrediction,
  onSelectBatch,
}: BatchesPageProps): JSX.Element {
  const {classes} = useStyles();
  const {classes: tableClasses} = useModelHubListingTableStyles();
  const hasOpenedBatchRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [sortField, setSortField] = useState<BatchSortField>('modifiedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const effectiveQuery = searchQuery === '' ? '' : debouncedQuery;

  // Reset offset when search or sort changes
  useEffect(() => {
    setOffset(0);
  }, [effectiveQuery, sortField, sortDirection]);

  const handleSort = useCallback(
    (field: string) => {
      const typedField = field as BatchSortField;
      if (typedField === sortField) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(typedField);
        setSortDirection('desc');
      }
    },
    [sortField]
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setOffset(0);
  }, []);

  const {batches, pagination, isLoading, error, refresh} = useBatchesSearch({
    limit: pageSize,
    offset,
    query: effectiveQuery,
    sort: {key: sortField, direction: sortDirection},
  });

  const sort = {field: sortField, direction: sortDirection};

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.currentTarget.value);
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
    setOffset(0);
  };

  const renderContent = (): JSX.Element => {
    if (error) {
      return (
        <Alert color="red" icon={<BenchlingIcon icon="exclamation-circle" />} title="Failed to load batches">
          <Group spacing="xs">
            <Text size="sm">{error}</Text>
            <Anchor component="button" onClick={refresh} size="sm" type="button">
              Retry
            </Anchor>
          </Group>
        </Alert>
      );
    }

    if (isLoading && batches.length === 0) {
      return <ModelHubLoadingState testProps={getTestProps({component: 'BatchesPage', element: 'loader'})} />;
    }

    if (batches.length === 0) {
      if (effectiveQuery) {
        return <ModelHubNoSearchResults entity="batches" onClearSearch={handleClearSearch} />;
      }
      return (
        <ModelHubEmptyState
          description="A batch is one or more predictions submitted together."
          illustration={BATCHES_ILLUSTRATION}
          onNewPrediction={onNewPrediction}
          title="No batches yet"
        />
      );
    }

    return (
      <Stack className={tableClasses.listingContent} spacing="lg">
        <Paper
          className={tableClasses.tablePaper}
          radius="md"
          withBorder={true}
          {...getTestProps({component: 'BatchesPage', element: 'table'})}
        >
          <Table
            className={tableClasses.table}
            horizontalSpacing={14}
            verticalSpacing={MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING}
          >
            <thead>
              <tr>
                <SortableColumnHeader field="name" label="Name" onSort={handleSort} sort={sort} />
                <th>Status</th>
                <th>Model</th>
                <SortableColumnHeader field="createdAt" label="Created" onSort={handleSort} sort={sort} />
                <SortableColumnHeader
                  field="modifiedAt"
                  label="Last modified"
                  onSort={handleSort}
                  sort={sort}
                />
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => {
                const model = requireScientificModel(catalogByExternalReference, batch.modelName);
                return (
                  <tr
                    key={batch.id}
                    className={classes.clickableRow}
                    onClick={() => {
                      if (hasOpenedBatchRef.current) {
                        return;
                      }
                      hasOpenedBatchRef.current = true;
                      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.BATCHES_BATCH_OPENED);
                      onSelectBatch(batch.id, batch.name);
                    }}
                  >
                    <td>
                      <Text className={classes.nameCell} size="lg">
                        {batch.name}
                      </Text>
                    </td>
                    <td>
                      <StatusBadge status={batch.status} />
                    </td>
                    <td>
                      <Text fw={600} size="lg">
                        {model.displayName}
                      </Text>
                    </td>
                    <td>
                      <Text color="dimmed" size="md">
                        {batch.createdAt ? readableTime(batch.createdAt) : '—'}
                      </Text>
                    </td>
                    <td>
                      <Text color="dimmed" size="md">
                        {batch.modifiedAt ? readableTime(batch.modifiedAt) : '—'}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Paper>
        <TablePagination
          onOffsetChange={setOffset}
          onPageSizeChange={handlePageSizeChange}
          pagination={pagination}
        />
      </Stack>
    );
  };

  return (
    <Stack
      className={classes.content}
      spacing="lg"
      {...getTestProps({component: 'BatchesPage', element: 'root'})}
    >
      <Stack spacing="md">
        <Group noWrap={true} pb={MODEL_HUB_MAIN_PADDING_Y_PX} position="apart">
          <Group spacing={12}>
            <Title order={1}>Batches</Title>
          </Group>
          {onContactUs != null && <GetInTouchButton onClick={onContactUs} />}
        </Group>

        <TextInput
          aria-label="Search batches"
          className={classes.searchInput}
          icon={<BenchlingIcon icon="search" />}
          onChange={handleSearchChange}
          placeholder="Search batches"
          size="lg"
          value={searchQuery}
        />
      </Stack>

      {renderContent()}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  clickableRow: {
    cursor: 'pointer',
  },
  content: {
    height: `calc(100vh - ${MODEL_HUB_MAIN_PADDING_Y_PX * 2}px)`,
    minHeight: 0,
  },
  nameCell: {
    fontFamily: theme.fontFamilyMonospace,
    fontWeight: 600,
  },
  searchInput: {
    maxWidth: 480,
    width: '100%',
  },
}));
